import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { referralAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Share2, DollarSign, TrendingUp, Eye, Copy, ExternalLink, 
  Loader2, LogOut, Clock, CheckCircle, XCircle, Link as LinkIcon,
  ArrowRight, Building2, CreditCard, History, Calendar, User,
  Wallet, BanknoteIcon, AlertCircle, Facebook, Instagram, Sparkles,
  Mail, MessageSquare, Phone, Bell, QrCode, Download, FileImage
} from 'lucide-react';

// Social Media Post Templates
const generatePostTemplate = (tour, link, platform) => {
  const url = `${window.location.origin}/r/${link.code}/${link.tour_id}`;
  const title = tour?.title || 'Amazing Tour';
  const price = tour?.price ? `$${tour.price}` : '';
  const duration = tour?.duration || '';
  
  const templates = {
    facebook: {
      text: `🌴 Discover Puerto Rico with this incredible tour!\n\n✨ ${title}\n${price ? `💰 Starting at ${price}\n` : ''}${duration ? `⏱️ ${duration}\n` : ''}\n🔥 Book now and create unforgettable memories!\n\n👉 ${url}\n\n#PuertoRico #Tours #Travel #Adventure #Vacation`,
      hashtags: ['#PuertoRico', '#Tours', '#Travel', '#Adventure']
    },
    instagram: {
      text: `🌴 YOUR PUERTO RICO ADVENTURE AWAITS! 🌴\n\n✨ ${title}\n\n${price ? `💰 ${price} per person\n` : ''}${duration ? `⏱️ Duration: ${duration}\n` : ''}\n📍 Puerto Rico\n\n🔗 Link in bio or click the link below!\n👉 ${url}\n\n.\n.\n.\n#PuertoRico #TourismPR #IslaDelEncanto #PRTravel #PuertoRicoTours #Caribbean #TravelGram #Wanderlust #Adventure #ExploreMore #VacationMode #TravelPuertoRico`,
      hashtags: ['#PuertoRico', '#TourismPR', '#IslaDelEncanto', '#PRTravel']
    },
    tiktok: {
      text: `POV: You just discovered the BEST tour in Puerto Rico 🌴✨\n\n${title}\n\n${price ? `Only ${price}! ` : ''}${duration ? `${duration} of pure adventure! ` : ''}\n\nLink in bio 👉 ${url}\n\n#PuertoRico #PuertoRicoTravel #TikTokTravel #TravelTikTok #IslaDelEncanto #Caribbean #AdventureAwaits #TravelTok #Vacation2026`,
      hashtags: ['#PuertoRico', '#TikTokTravel', '#IslaDelEncanto']
    },
    twitter: {
      text: `🌴 ${title}\n\n${price ? `💰 ${price} | ` : ''}${duration ? `⏱️ ${duration}` : ''}\n\nBook your Puerto Rico adventure now! 🇵🇷\n\n👉 ${url}\n\n#PuertoRico #Travel`,
      hashtags: ['#PuertoRico', '#Travel']
    },
    whatsapp: {
      text: `🌴 Hey! Check out this amazing tour in Puerto Rico!\n\n✨ *${title}*\n${price ? `💰 ${price} per person\n` : ''}${duration ? `⏱️ ${duration}\n` : ''}\n📍 Book here: ${url}\n\nLet me know if you want to join! 🎉`,
      hashtags: []
    },
    email: {
      text: `Hi!\n\nI found this amazing tour in Puerto Rico and thought you might be interested:\n\n🌴 ${title}\n${price ? `💰 Price: ${price} per person\n` : ''}${duration ? `⏱️ Duration: ${duration}\n` : ''}\n📍 Book here: ${url}\n\nThis looks like an incredible experience! Let me know what you think.\n\nBest regards`,
      hashtags: []
    },
    sms: {
      text: `🌴 ${title}\n${price ? `${price} ` : ''}${duration ? `• ${duration}` : ''}\n\nBook: ${url}`,
      hashtags: []
    }
  };
  
  return templates[platform] || templates.facebook;
};

export default function ReferralAgentDashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('links');
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  
  // Share Templates state
  const [shareTemplates, setShareTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Profile/Leaderboard settings
  const [profileSettings, setProfileSettings] = useState({
    show_on_leaderboard: true,
    display_name: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Bank info state
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankInfo, setBankInfo] = useState({
    bank_name: '',
    account_holder: '',
    account_number: '',
    routing_number: '',
    account_type: 'checking',
  });

  // Social share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('facebook');
  const [postTemplate, setPostTemplate] = useState('');
  
  // QR Code dialog state
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrLink, setQRLink] = useState(null);
  const qrRef = useRef(null);

  useEffect(() => {
    // Check if user is a referral agent using main auth
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (user.role !== 'referral_agent') {
      toast.error('Access denied. This page is for referral agents only.');
      navigate('/');
      return;
    }
    
    loadDashboard();
  }, [user, navigate]);

  const loadDashboard = async () => {
    try {
      const res = await referralAPI.getDashboard();
      setDashboardData(res.data);
      
      // Load bank info if available
      if (res.data?.agent?.bank_info) {
        setBankInfo(res.data.agent.bank_info);
      }
      
      // Load notifications for this agent (congrats messages, etc.)
      // These come from successful conversions
      const conversions = res.data?.conversions || [];
      const recentNotifications = conversions
        .filter(c => c.status === 'pending' || c.status === 'payout_approved')
        .slice(0, 5)
        .map(c => ({
          id: c.id,
          type: 'conversion',
          title: '🎉 New Booking!',
          message: `You earned $${(c.commission_amount || 0).toFixed(2)} commission from ${c.booker_name || 'a customer'}`,
          date: c.created_at
        }));
      setNotifications(recentNotifications);
      
      // Load share templates
      try {
        const templatesRes = await referralAPI.getShareTemplates();
        setShareTemplates(templatesRes.data || []);
      } catch (e) {
        console.log('Using default templates');
      }
      
      // Load profile settings
      try {
        const profileRes = await referralAPI.getProfile();
        if (profileRes.data) {
          setProfileSettings({
            show_on_leaderboard: profileRes.data.show_on_leaderboard !== false,
            display_name: profileRes.data.display_name || ''
          });
        }
      } catch (e) {
        console.log('Using default profile settings');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate('/login');
      }
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const copyLink = (link) => {
    const url = `${window.location.origin}/r/${link.code}/${link.tour_id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };
  
  // Save share template
  const handleSaveTemplate = async (template) => {
    setSavingTemplate(true);
    try {
      if (template.id && !template.id.startsWith('default_')) {
        await referralAPI.updateShareTemplate(template.id, {
          name: template.name,
          message: template.message
        });
      } else {
        // Creating new or updating default template
        await referralAPI.updateShareTemplate(template.id, {
          name: template.name,
          message: template.message
        });
      }
      toast.success('Template saved!');
      setEditingTemplate(null);
      // Reload templates
      const templatesRes = await referralAPI.getShareTemplates();
      setShareTemplates(templatesRes.data || []);
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };
  
  // Save profile settings
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await referralAPI.updateProfile(profileSettings);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Open share dialog with template
  const openShareDialog = (link, platform) => {
    setShareLink(link);
    setSelectedPlatform(platform);
    const template = generatePostTemplate(link.tour, link, platform);
    setPostTemplate(template.text);
    setShowShareDialog(true);
  };

  // Copy post template
  const copyPostTemplate = () => {
    navigator.clipboard.writeText(postTemplate);
    toast.success('Post template copied! Ready to paste on ' + selectedPlatform);
  };

  // Open QR code dialog
  const openQRDialog = (link) => {
    setQRLink(link);
    setShowQRDialog(true);
  };

  // Download QR code as PNG
  const downloadQRCode = () => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;
    
    // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    // Set canvas size (higher resolution for better print quality)
    canvas.width = 400;
    canvas.height = 480; // Extra space for text
    
    img.onload = () => {
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code centered
      ctx.drawImage(img, 50, 20, 300, 300);
      
      // Add tour title
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      const tourTitle = qrLink?.tour?.title || 'Tour';
      ctx.fillText(tourTitle.substring(0, 30) + (tourTitle.length > 30 ? '...' : ''), canvas.width / 2, 350);
      
      // Add "Scan to Book" text
      ctx.font = '14px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.fillText('Scan to Book', canvas.width / 2, 380);
      
      // Add referral code
      ctx.font = 'bold 12px monospace';
      ctx.fillStyle = '#3b82f6';
      ctx.fillText(`Code: ${qrLink?.code || ''}`, canvas.width / 2, 410);
      
      // Add URL
      ctx.font = '10px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#94a3b8';
      const shortUrl = `${window.location.host}/r/${qrLink?.code}/${qrLink?.tour_id}`;
      ctx.fillText(shortUrl, canvas.width / 2, 440);
      
      // Download
      const link = document.createElement('a');
      link.download = `qr-${qrLink?.tour?.title?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'tour'}-${qrLink?.code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('QR code downloaded!');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Generate print-ready marketing flyer
  const downloadFlyer = async () => {
    if (!qrRef.current || !qrLink) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;
    
    toast.loading('Generating flyer...');
    
    // Create canvas for flyer (Letter size at 150 DPI for print)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Flyer dimensions (8.5x11 inches at 150 DPI = 1275x1650 px)
    canvas.width = 1275;
    canvas.height = 1650;
    
    const tour = qrLink.tour || {};
    const tourTitle = tour.title || 'Amazing Tour';
    const tourPrice = tour.price ? `$${tour.price}` : 'Contact for pricing';
    const tourImage = tour.images?.[0] || null;
    
    // Load tour image if available
    const loadImage = (src) => {
      return new Promise((resolve) => {
        if (!src) {
          resolve(null);
          return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });
    };
    
    const tourImg = await loadImage(tourImage);
    const svgData = new XMLSerializer().serializeToString(svg);
    const qrImg = new Image();
    
    qrImg.onload = () => {
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#1e3a5f');
      gradient.addColorStop(0.4, '#2563eb');
      gradient.addColorStop(1, '#1e40af');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add decorative elements
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.beginPath();
      ctx.arc(-100, -100, 400, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(canvas.width + 100, canvas.height + 100, 500, 0, Math.PI * 2);
      ctx.fill();
      
      // Tour Image (top half)
      if (tourImg) {
        // Create rounded rect clip for image
        const imgY = 80;
        const imgWidth = canvas.width - 100;
        const imgHeight = 500;
        const imgX = 50;
        
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(imgX, imgY, imgWidth, imgHeight, 20);
        ctx.clip();
        
        // Draw image maintaining aspect ratio (cover)
        const imgAspect = tourImg.width / tourImg.height;
        const targetAspect = imgWidth / imgHeight;
        let drawWidth, drawHeight, drawX, drawY;
        
        if (imgAspect > targetAspect) {
          drawHeight = imgHeight;
          drawWidth = imgHeight * imgAspect;
          drawX = imgX - (drawWidth - imgWidth) / 2;
          drawY = imgY;
        } else {
          drawWidth = imgWidth;
          drawHeight = imgWidth / imgAspect;
          drawX = imgX;
          drawY = imgY - (drawHeight - imgHeight) / 2;
        }
        
        ctx.drawImage(tourImg, drawX, drawY, drawWidth, drawHeight);
        ctx.restore();
        
        // Price badge on image
        ctx.fillStyle = '#16a34a';
        ctx.beginPath();
        ctx.roundRect(imgX + 20, imgY + 20, 180, 50, 25);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(tourPrice, imgX + 40, imgY + 55);
      }
      
      // Tour Title
      ctx.fillStyle = 'white';
      ctx.font = 'bold 56px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      
      // Word wrap title
      const maxWidth = canvas.width - 100;
      const words = tourTitle.split(' ');
      let line = '';
      let y = tourImg ? 650 : 200;
      const lineHeight = 65;
      
      for (let word of words) {
        const testLine = line + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line !== '') {
          ctx.fillText(line.trim(), canvas.width / 2, y);
          line = word + ' ';
          y += lineHeight;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line.trim(), canvas.width / 2, y);
      
      // Subtitle
      ctx.font = '28px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText('Book Your Experience Today!', canvas.width / 2, y + 60);
      
      // QR Code section (white card)
      const qrCardY = y + 100;
      const qrCardHeight = 450;
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.roundRect(50, qrCardY, canvas.width - 100, qrCardHeight, 30);
      ctx.fill();
      
      // Shadow effect
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = 10;
      
      // Draw QR code
      const qrSize = 280;
      const qrX = (canvas.width - qrSize) / 2;
      const qrY = qrCardY + 40;
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      
      // QR instruction text
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
      ctx.fillText('📱 Scan to Book', canvas.width / 2, qrY + qrSize + 50);
      
      // Referral code
      ctx.font = '22px monospace';
      ctx.fillStyle = '#3b82f6';
      ctx.fillText(`Code: ${qrLink.code}`, canvas.width / 2, qrY + qrSize + 90);
      
      // URL
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#64748b';
      const displayUrl = `${window.location.host}/r/${qrLink.code}/${qrLink.tour_id}`;
      ctx.fillText(displayUrl, canvas.width / 2, qrY + qrSize + 120);
      
      // Footer
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = '20px system-ui, -apple-system, sans-serif';
      ctx.fillText('Puerto Rico Tours • Unforgettable Experiences', canvas.width / 2, canvas.height - 40);
      
      // Download
      const link = document.createElement('a');
      link.download = `flyer-${tourTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${qrLink.code}.png`;
      link.href = canvas.toDataURL('image/png', 0.95);
      link.click();
      
      toast.dismiss();
      toast.success('Flyer downloaded! Print at 8.5x11" for best quality.');
    };
    
    qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Share to social media
  const shareToSocial = (platform) => {
    if (!shareLink) return;
    const url = `${window.location.origin}/r/${shareLink.code}/${shareLink.tour_id}`;
    const fullText = postTemplate; // Use the full template text
    const encodedText = encodeURIComponent(fullText);
    const encodedUrl = encodeURIComponent(url);
    
    switch (platform) {
      case 'facebook':
        // Facebook share - use feed dialog for better text support
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank', 'width=600,height=500');
        // Also copy text for manual paste
        navigator.clipboard.writeText(fullText);
        toast.success('Link shared! Caption copied to clipboard for adding to your post.');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank', 'width=600,height=400');
        break;
      case 'whatsapp':
        // WhatsApp with full template including URL
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
        break;
      case 'instagram':
        // Instagram doesn't support direct URL sharing, so copy to clipboard
        navigator.clipboard.writeText(fullText);
        toast.success('Caption copied! Open Instagram and paste in your post.');
        break;
      case 'tiktok':
        // TikTok doesn't support direct URL sharing, so copy to clipboard
        navigator.clipboard.writeText(fullText);
        toast.success('Caption copied! Open TikTok and paste in your video description.');
        break;
      case 'email':
        // Email with proper subject and body
        const tourTitle = shareLink.tour?.title || 'Amazing Tour';
        const emailSubject = encodeURIComponent(`Check out this amazing tour: ${tourTitle}`);
        const emailBody = encodeURIComponent(fullText);
        window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
        break;
      case 'sms':
        // SMS with full template (shortened for SMS)
        const smsText = `🌴 ${shareLink.tour?.title || 'Amazing Tour'}\n\nBook now: ${url}`;
        window.location.href = `sms:?body=${encodeURIComponent(smsText)}`;
        break;
      default:
        copyPostTemplate();
    }
  };

  const quickShare = async (link, method) => {
    const url = `${window.location.origin}/r/${link.code}/${link.tour_id}`;
    const tourTitle = link.tour?.title || 'Amazing Tour';
    const price = link.tour?.price ? ` - $${link.tour.price}` : '';
    const text = `🌴 Check out this amazing tour: ${tourTitle}${price}`;
    const fullText = `${text}\n\n📍 Book now: ${url}`;
    
    if (method === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(fullText)}`, '_blank');
    } else if (method === 'email') {
      const emailSubject = encodeURIComponent(`Check out: ${tourTitle}`);
      const emailBody = encodeURIComponent(`Hi!\n\nI found this amazing tour and thought you might be interested:\n\n🌴 ${tourTitle}${price}\n\n📍 Book here: ${url}\n\nLet me know what you think!`);
      window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
    } else if (method === 'sms') {
      const smsText = `Hey! Check out this tour: ${tourTitle}${price}\n\nBook: ${url}`;
      window.location.href = `sms:?body=${encodeURIComponent(smsText)}`;
    } else if (method === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=500');
      navigator.clipboard.writeText(fullText);
      toast.success('Link shared! Caption copied to clipboard.');
    } else if (method === 'instagram') {
      navigator.clipboard.writeText(`${text}\n\n🔗 Link in bio!\n👉 ${url}\n\n#PuertoRico #Tours #Travel #Adventure`);
      toast.success('Caption copied! Open Instagram and paste in your post.');
    } else if (navigator.share) {
      try {
        await navigator.share({ title: tourTitle, text, url });
      } catch (e) {
        copyLink(link);
      }
    } else {
      copyLink(link);
    }
  };

  const saveBankInfo = async () => {
    if (!bankInfo.bank_name || !bankInfo.account_holder || !bankInfo.account_number) {
      toast.error('Please fill in required bank details');
      return;
    }
    
    setSavingBank(true);
    try {
      await referralAPI.updateBankInfo(bankInfo);
      toast.success('Bank information saved successfully');
      setShowBankDialog(false);
      loadDashboard();
    } catch (error) {
      toast.error('Failed to save bank information');
    } finally {
      setSavingBank(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
      case 'payout_approved':
        return <Badge className="gap-1 bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" />Approved</Badge>;
      case 'paid':
        return <Badge className="gap-1 bg-blue-100 text-blue-800"><DollarSign className="h-3 w-3" />Paid</Badge>;
      case 'cancelled':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const links = dashboardData?.links || [];
  const conversions = dashboardData?.conversions || [];
  const agentInfo = dashboardData?.agent || user;
  
  // Separate conversions by status
  const pendingPayouts = conversions.filter(c => c.status === 'pending');
  const approvedPayouts = conversions.filter(c => c.status === 'payout_approved');
  const paidPayouts = conversions.filter(c => c.status === 'paid');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <Share2 className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Referral Agent Portal</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Welcome, <strong>{agentInfo?.name}</strong>
            </span>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Clicks</p>
                <p className="text-2xl font-bold">{stats.total_clicks || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bookings</p>
                <p className="text-2xl font-bold">{stats.total_bookings || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">${(stats.pending_payout || 0).toFixed(2)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold text-green-600">${(stats.approved_payout || 0).toFixed(2)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Wallet className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold">${(stats.paid_out || 0).toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* QR Scan vs Link Click Analytics */}
        {(stats.qr_scans > 0 || stats.link_clicks > 0) && (
          <Card className="p-4 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <QrCode className="h-5 w-5 text-purple-600" />
                Traffic Analytics
              </h3>
              <Badge variant="outline" className="text-xs">
                {stats.total_clicks || 0} total clicks
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <QrCode className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">QR Code Scans</span>
                </div>
                <p className="text-3xl font-bold text-purple-700">{stats.qr_scans || 0}</p>
                <p className="text-xs text-purple-600 mt-1">
                  {stats.total_clicks > 0 ? Math.round((stats.qr_scans || 0) / stats.total_clicks * 100) : 0}% of total traffic
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <LinkIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Link Clicks</span>
                </div>
                <p className="text-3xl font-bold text-blue-700">{stats.link_clicks || 0}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {stats.total_clicks > 0 ? Math.round((stats.link_clicks || 0) / stats.total_clicks * 100) : 0}% of total traffic
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              QR scans are tracked when customers scan your printed QR codes
            </p>
          </Card>
        )}

        {/* Agent Info & Bank Details */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Your Account
              </h2>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Referral Code</span>
                <code className="px-2 py-1 bg-primary/10 rounded text-primary font-mono text-sm">
                  {agentInfo?.referral_code}
                </code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span>{agentInfo?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active Links</span>
                <span>{links.length}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <BanknoteIcon className="h-5 w-5" />
                Payout Information
              </h2>
              <Button variant="outline" size="sm" onClick={() => setShowBankDialog(true)}>
                {bankInfo.bank_name ? 'Edit' : 'Add'} Bank Info
              </Button>
            </div>
            
            {bankInfo.bank_name ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bank</span>
                  <span>{bankInfo.bank_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Holder</span>
                  <span>{bankInfo.account_holder}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account</span>
                  <span>****{bankInfo.account_number?.slice(-4)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="capitalize">{bankInfo.account_type}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                <p className="text-muted-foreground text-sm">
                  Add your bank details to receive monthly payouts
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-fit mb-6">
            <TabsTrigger value="links" className="gap-2">
              <LinkIcon className="h-4 w-4" />
              My Links
            </TabsTrigger>
            <TabsTrigger value="commissions" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Commissions
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="h-4 w-4" />
              Payout History
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2 relative">
              <Bell className="h-4 w-4" />
              Notifications
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                  {notifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2">
              <Sparkles className="h-4 w-4" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <User className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* My Links Tab */}
          <TabsContent value="links">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4">Your Referral Links</h3>
              
              {links.length === 0 ? (
                <div className="text-center py-8">
                  <LinkIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No referral links yet.</p>
                  <p className="text-sm text-muted-foreground">Contact the admin to get your unique referral links.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {links.map((link) => (
                    <div key={link.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1">
                          {link.tour?.images?.[0] && (
                            <img src={link.tour.images[0]} alt="" className="w-20 h-14 rounded-lg object-cover" />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold">{link.tour?.title || 'Tour'}</h4>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                {link.clicks || 0} clicks
                              </span>
                              {(link.qr_scans > 0 || link.link_clicks > 0) && (
                                <>
                                  <span className="flex items-center gap-1 text-purple-600">
                                    <QrCode className="h-3 w-3" />
                                    {link.qr_scans || 0} QR
                                  </span>
                                  <span className="flex items-center gap-1 text-blue-600">
                                    <LinkIcon className="h-3 w-3" />
                                    {link.link_clicks || 0} links
                                  </span>
                                </>
                              )}
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                {link.conversion_count || 0} bookings
                              </span>
                            </div>
                            <div className="mt-2 p-2 bg-muted rounded flex items-center gap-2">
                              <code className="text-xs flex-1 truncate">
                                {window.location.origin}/r/{link.code}/{link.tour_id}
                              </code>
                              <Button variant="ghost" size="sm" onClick={() => copyLink(link)}>
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button size="sm" onClick={() => copyLink(link)} className="gap-2">
                            <Copy className="h-4 w-4" />
                            Copy Link
                          </Button>
                          
                          {/* Social Media Share Buttons */}
                          <div className="flex gap-1 flex-wrap">
                            {/* Facebook */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openShareDialog(link, 'facebook')} 
                              title="Share to Facebook"
                              className="bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border-[#1877F2]/30"
                            >
                              <Facebook className="h-4 w-4 text-[#1877F2]" />
                            </Button>
                            
                            {/* Instagram */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openShareDialog(link, 'instagram')} 
                              title="Share to Instagram"
                              className="bg-gradient-to-tr from-[#F58529]/10 via-[#DD2A7B]/10 to-[#8134AF]/10 hover:from-[#F58529]/20 hover:via-[#DD2A7B]/20 hover:to-[#8134AF]/20"
                            >
                              <Instagram className="h-4 w-4 text-[#DD2A7B]" />
                            </Button>
                            
                            {/* TikTok */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openShareDialog(link, 'tiktok')} 
                              title="Share to TikTok"
                              className="bg-black/5 hover:bg-black/10 border-black/20"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                              </svg>
                            </Button>
                            
                            {/* WhatsApp */}
                            <Button variant="outline" size="sm" onClick={() => quickShare(link, 'whatsapp')} title="Share via WhatsApp" className="bg-[#25D366]/10 hover:bg-[#25D366]/20 border-[#25D366]/30">
                              <svg className="h-4 w-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </Button>
                            
                            {/* Email */}
                            <Button variant="outline" size="sm" onClick={() => quickShare(link, 'email')} title="Share via Email">
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="2" y="4" width="20" height="16" rx="2"/>
                                <path d="M22 7l-10 7L2 7"/>
                              </svg>
                            </Button>
                            
                            {/* SMS */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => quickShare(link, 'sms')} 
                              title="Share via SMS"
                              className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                            >
                              <MessageSquare className="h-4 w-4 text-blue-600" />
                            </Button>
                            
                            {/* QR Code */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openQRDialog(link)} 
                              title="Generate QR Code"
                              className="bg-purple-50 hover:bg-purple-100 border-purple-200"
                            >
                              <QrCode className="h-4 w-4 text-purple-600" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <div className="space-y-6">
              {/* Upcoming Payouts (Approved) */}
              <Card className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Approved Payouts (Ready for Payment)
                </h3>
                
                {approvedPayouts.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No approved payouts yet</p>
                ) : (
                  <div className="space-y-3">
                    {approvedPayouts.map((conv) => (
                      <div key={conv.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{conv.tour_name}</p>
                          <p className="text-sm text-muted-foreground">
                            Booked by {conv.booker_name} • Tour: {conv.tour_date}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600 text-lg">${(conv.commission_amount || 0).toFixed(2)}</p>
                          {getStatusBadge(conv.status)}
                        </div>
                      </div>
                    ))}
                    <div className="pt-4 border-t flex justify-between items-center">
                      <span className="font-medium">Total Approved</span>
                      <span className="text-xl font-bold text-green-600">
                        ${approvedPayouts.reduce((sum, c) => sum + (c.commission_amount || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </Card>

              {/* Pending Payouts */}
              <Card className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" />
                  Pending Commissions (Awaiting Tour Completion)
                </h3>
                
                {pendingPayouts.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No pending commissions</p>
                ) : (
                  <div className="space-y-3">
                    {pendingPayouts.map((conv) => (
                      <div key={conv.id} className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium">{conv.tour_name}</p>
                          <p className="text-sm text-muted-foreground">
                            Booked by {conv.booker_name} • Tour: {conv.tour_date}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-amber-600">${(conv.commission_amount || 0).toFixed(2)}</p>
                          {getStatusBadge(conv.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Payout History Tab */}
          <TabsContent value="history">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <History className="h-5 w-5" />
                Payout History
              </h3>
              
              {paidPayouts.length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No payout history yet</p>
                  <p className="text-sm text-muted-foreground">Completed payouts will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paidPayouts.map((conv) => (
                    <div key={conv.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">{conv.tour_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Paid on {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-blue-600">${(conv.commission_amount || 0).toFixed(2)}</p>
                        {getStatusBadge(conv.status)}
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t flex justify-between items-center">
                    <span className="font-medium">Total Paid Out</span>
                    <span className="text-xl font-bold text-blue-600">
                      ${paidPayouts.reduce((sum, c) => sum + (c.commission_amount || 0), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Recent Notifications
              </h3>
              
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No new notifications</p>
                  <p className="text-sm text-muted-foreground">You'll see congrats messages and updates here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div key={notif.id} className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="p-2 bg-green-100 rounded-full">
                        <Sparkles className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-green-900">{notif.title}</p>
                        <p className="text-sm text-green-800">{notif.message}</p>
                        <p className="text-xs text-green-600 mt-1">
                          {notif.date ? new Date(notif.date).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick Share Section */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Share2 className="h-4 w-4" />
                  Share Your Links
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Share your referral links via your favorite platform:
                </p>
                <div className="flex flex-wrap gap-2">
                  {links.slice(0, 3).map((link) => (
                    <div key={link.id} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                      <span className="text-sm font-medium truncate max-w-[150px]">{link.tour?.title}</span>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => quickShare(link, 'whatsapp')}
                          title="Share via WhatsApp"
                        >
                          <MessageSquare className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => quickShare(link, 'email')}
                          title="Share via Email"
                        >
                          <Mail className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => quickShare(link, 'sms')}
                          title="Share via SMS"
                        >
                          <Phone className="h-4 w-4 text-purple-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => copyLink(link)}
                          title="Copy Link"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Share Templates Tab */}
          <TabsContent value="templates">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Customize Your Share Messages
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Personalize the messages you use when sharing tours. Use variables like <code className="bg-muted px-1 rounded">{'{{tour_name}}'}</code>, <code className="bg-muted px-1 rounded">{'{{price}}'}</code>, and <code className="bg-muted px-1 rounded">{'{{link}}'}</code>.
              </p>
              
              <div className="space-y-4">
                {shareTemplates.map((template) => (
                  <div key={template.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {template.channel === 'whatsapp' && <MessageSquare className="h-4 w-4 text-green-600" />}
                        {template.channel === 'email' && <Mail className="h-4 w-4 text-blue-600" />}
                        {template.channel === 'sms' && <Phone className="h-4 w-4 text-purple-600" />}
                        <span className="font-medium">{template.name}</span>
                        {template.is_default && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingTemplate({...template})}
                      >
                        Edit
                      </Button>
                    </div>
                    <div className="bg-muted p-3 rounded text-sm whitespace-pre-wrap">
                      {template.message.substring(0, 150)}{template.message.length > 150 ? '...' : ''}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Template Editor Dialog */}
              {editingTemplate && (
                <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Edit Template</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Template Name</Label>
                        <Input 
                          value={editingTemplate.name}
                          onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Message</Label>
                        <Textarea 
                          value={editingTemplate.message}
                          onChange={(e) => setEditingTemplate({...editingTemplate, message: e.target.value})}
                          rows={8}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Variables: {'{{tour_name}}'}, {'{{price}}'}, {'{{link}}'}, {'{{duration}}'}
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                        Cancel
                      </Button>
                      <Button onClick={() => handleSaveTemplate(editingTemplate)} disabled={savingTemplate}>
                        {savingTemplate && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save Template
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Settings
              </h3>
              
              <div className="space-y-6">
                {/* Leaderboard Settings */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-4">Leaderboard Visibility</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Show on Public Leaderboard</Label>
                        <p className="text-sm text-muted-foreground">Allow your profile to appear on the top agents leaderboard</p>
                      </div>
                      <Button
                        variant={profileSettings.show_on_leaderboard ? "default" : "outline"}
                        size="sm"
                        onClick={() => setProfileSettings({...profileSettings, show_on_leaderboard: !profileSettings.show_on_leaderboard})}
                      >
                        {profileSettings.show_on_leaderboard ? 'Visible' : 'Hidden'}
                      </Button>
                    </div>
                    <div>
                      <Label>Display Name (optional)</Label>
                      <p className="text-xs text-muted-foreground mb-2">Custom name shown on leaderboard. Leave blank to use masked name.</p>
                      <Input 
                        placeholder="e.g., John D. or TravelPro123"
                        value={profileSettings.display_name}
                        onChange={(e) => setProfileSettings({...profileSettings, display_name: e.target.value})}
                      />
                    </div>
                    <Button onClick={handleSaveProfile} disabled={savingProfile}>
                      {savingProfile && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save Settings
                    </Button>
                  </div>
                </div>
                
                {/* Account Info */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-4">Account Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name:</span>
                      <span>{agentInfo?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <span>{agentInfo?.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Referral Code:</span>
                      <code className="bg-muted px-2 py-0.5 rounded">{agentInfo?.referral_code}</code>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Payout Info Box */}
        <Card className="mt-8 p-4 bg-blue-50 border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Payout Information
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Pending:</strong> Commission earned, waiting for the tour to complete</li>
            <li>• <strong>Approved:</strong> Tour completed, ready for monthly payout</li>
            <li>• <strong>Paid:</strong> Commission has been transferred to your bank account</li>
            <li>• Payouts are processed monthly on the 1st of each month</li>
            <li>• Make sure your bank information is up to date to receive payments</li>
          </ul>
        </Card>
      </main>

      {/* Bank Info Dialog */}
      <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BanknoteIcon className="h-5 w-5" />
              Bank Account Information
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={bankInfo.bank_name}
                onChange={(e) => setBankInfo({ ...bankInfo, bank_name: e.target.value })}
                placeholder="e.g., Bank of America"
              />
            </div>
            
            <div>
              <Label>Account Holder Name *</Label>
              <Input
                value={bankInfo.account_holder}
                onChange={(e) => setBankInfo({ ...bankInfo, account_holder: e.target.value })}
                placeholder="Full name on account"
              />
            </div>
            
            <div>
              <Label>Account Number *</Label>
              <Input
                value={bankInfo.account_number}
                onChange={(e) => setBankInfo({ ...bankInfo, account_number: e.target.value })}
                placeholder="Account number"
              />
            </div>
            
            <div>
              <Label>Routing Number</Label>
              <Input
                value={bankInfo.routing_number}
                onChange={(e) => setBankInfo({ ...bankInfo, routing_number: e.target.value })}
                placeholder="9-digit routing number"
              />
            </div>
            
            <div>
              <Label>Account Type</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={bankInfo.account_type === 'checking'}
                    onChange={() => setBankInfo({ ...bankInfo, account_type: 'checking' })}
                  />
                  Checking
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={bankInfo.account_type === 'savings'}
                    onChange={() => setBankInfo({ ...bankInfo, account_type: 'savings' })}
                  />
                  Savings
                </label>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Your bank information is encrypted and secure. Payouts are processed monthly.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankDialog(false)}>Cancel</Button>
            <Button onClick={saveBankInfo} disabled={savingBank}>
              {savingBank && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Bank Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Media Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Share to {selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1)}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Platform Selector */}
            <div className="flex gap-2 flex-wrap">
              {['facebook', 'instagram', 'tiktok', 'twitter', 'whatsapp', 'email', 'sms'].map((platform) => (
                <Button
                  key={platform}
                  variant={selectedPlatform === platform ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setSelectedPlatform(platform);
                    if (shareLink) {
                      const template = generatePostTemplate(shareLink.tour, shareLink, platform);
                      setPostTemplate(template.text);
                    }
                  }}
                  className="capitalize"
                >
                  {platform === 'facebook' && <Facebook className="h-4 w-4 mr-1" />}
                  {platform === 'instagram' && <Instagram className="h-4 w-4 mr-1" />}
                  {platform === 'tiktok' && (
                    <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  )}
                  {platform === 'twitter' && (
                    <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  )}
                  {platform === 'whatsapp' && (
                    <svg className="h-4 w-4 mr-1 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  )}
                  {platform === 'email' && <Mail className="h-4 w-4 mr-1" />}
                  {platform === 'sms' && <MessageSquare className="h-4 w-4 mr-1" />}
                  {platform}
                </Button>
              ))}
            </div>

            {/* Tour Preview */}
            {shareLink?.tour && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                {shareLink.tour.images?.[0] && (
                  <img src={shareLink.tour.images[0]} alt="" className="w-16 h-12 rounded object-cover" />
                )}
                <div>
                  <p className="font-medium text-sm">{shareLink.tour.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {shareLink.tour.price && `$${shareLink.tour.price}`}
                    {shareLink.tour.duration && ` • ${shareLink.tour.duration}`}
                  </p>
                </div>
              </div>
            )}

            {/* Post Template */}
            <div>
              <Label className="mb-2 block">Post Template (Editable)</Label>
              <Textarea
                value={postTemplate}
                onChange={(e) => setPostTemplate(e.target.value)}
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Edit the template above, then copy or share directly!
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button onClick={copyPostTemplate} variant="outline" className="gap-2">
                <Copy className="h-4 w-4" />
                Copy Caption
              </Button>
              <Button onClick={() => shareToSocial(selectedPlatform)} className="gap-2">
                <Share2 className="h-4 w-4" />
                {selectedPlatform === 'instagram' || selectedPlatform === 'tiktok' 
                  ? 'Copy & Open App' 
                  : `Share to ${selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1)}`
                }
              </Button>
            </div>

            {/* Platform Tips */}
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              {selectedPlatform === 'instagram' && (
                <p><strong>Instagram Tip:</strong> Copy the caption, then open Instagram. Create a post or story and paste the caption. Add your referral link to your bio!</p>
              )}
              {selectedPlatform === 'tiktok' && (
                <p><strong>TikTok Tip:</strong> Copy the caption, then open TikTok. Record a video showcasing the tour and paste the caption in the description. Add your link to your bio!</p>
              )}
              {selectedPlatform === 'facebook' && (
                <p><strong>Facebook Tip:</strong> Share directly to your feed or copy the text to create a more engaging post with photos!</p>
              )}
              {selectedPlatform === 'twitter' && (
                <p><strong>X/Twitter Tip:</strong> The post is optimized for Twitter's character limit. Share directly or customize!</p>
              )}
              {selectedPlatform === 'whatsapp' && (
                <p><strong>WhatsApp Tip:</strong> Opens WhatsApp with the message pre-filled. Send to friends, family, or share to your Status!</p>
              )}
              {selectedPlatform === 'email' && (
                <p><strong>Email Tip:</strong> Opens your email app with subject and body pre-filled. Perfect for sharing with friends and family!</p>
              )}
              {selectedPlatform === 'sms' && (
                <p><strong>SMS Tip:</strong> Opens your messaging app with the link. Great for quick shares to contacts!</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-purple-600" />
              QR Code for {qrLink?.tour?.title || 'Tour'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col items-center space-y-6 py-4">
            {/* QR Code Container */}
            <div 
              ref={qrRef}
              className="bg-white p-6 rounded-xl shadow-lg border-2 border-purple-100"
            >
              <QRCodeSVG
                value={qrLink ? `${window.location.origin}/r/${qrLink.code}/${qrLink.tour_id}?source=qr` : ''}
                size={200}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#1e293b"
              />
            </div>

            {/* Tour Info */}
            <div className="text-center space-y-1">
              <p className="font-semibold text-lg">{qrLink?.tour?.title}</p>
              <p className="text-sm text-muted-foreground">Scan to view and book</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Badge variant="secondary" className="font-mono">
                  Code: {qrLink?.code}
                </Badge>
              </div>
            </div>

            {/* URL Display */}
            <div className="w-full bg-muted rounded-lg p-3">
              <p className="text-xs text-muted-foreground text-center font-mono break-all">
                {qrLink ? `${window.location.origin}/r/${qrLink.code}/${qrLink.tour_id}?source=qr` : ''}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 w-full">
              <Button 
                variant="outline" 
                className="flex-1 gap-2"
                onClick={() => {
                  const url = `${window.location.origin}/r/${qrLink?.code}/${qrLink?.tour_id}?source=qr`;
                  navigator.clipboard.writeText(url);
                  toast.success('Link copied!');
                }}
              >
                <Copy className="h-4 w-4" />
                Copy Link
              </Button>
              <Button 
                className="flex-1 gap-2 bg-purple-600 hover:bg-purple-700"
                onClick={downloadQRCode}
              >
                <Download className="h-4 w-4" />
                Download QR
              </Button>
            </div>

            {/* Print-Ready Flyer Button */}
            <Button 
              className="w-full gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              onClick={downloadFlyer}
            >
              <FileImage className="h-4 w-4" />
              Generate Print-Ready Flyer (8.5x11")
            </Button>

            {/* Usage Tips */}
            <div className="bg-purple-50 rounded-lg p-4 w-full">
              <p className="text-sm font-medium text-purple-900 mb-2">💡 How to use your QR code:</p>
              <ul className="text-xs text-purple-700 space-y-1">
                <li>• Print and display at your business or events</li>
                <li>• Add to your business cards or flyers</li>
                <li>• Share in group chats or presentations</li>
                <li>• Include in email signatures</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
